/**
 * API client for the Dealer Plates tab on the Photo Configuration page.
 * Two coupled resources hang off this file: the per-group brand logo
 * (one image per dealer group that the generator draws from) and the
 * per-group plates (many plates per group, exactly one marked default
 * for the Phase 4 worker to pick up).
 *
 * Response shapes mirror backend/schemas.py — keep them in lockstep.
 */
import { API } from "../config/api"

const TOKEN_KEY = "invi_token"

function getToken(): string {
  return localStorage.getItem(TOKEN_KEY) ?? ""
}

async function authFetch(url: string, init?: RequestInit): Promise<Response> {
  const res = await fetch(url, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getToken()}`,
      ...(init?.headers ?? {}),
    },
  })
  if (!res.ok) {
    const body = await res.text()
    throw new Error(body || `HTTP ${res.status}`)
  }
  return res
}

async function authFetchMultipart(
  url: string, formData: FormData, method: "POST" | "PUT" = "POST",
): Promise<Response> {
  const res = await fetch(url, {
    method,
    body: formData,
    headers: {
      Authorization: `Bearer ${getToken()}`,
    },
  })
  if (!res.ok) {
    const body = await res.text()
    throw new Error(body || `HTTP ${res.status}`)
  }
  return res
}

// --- Types (mirror backend/schemas.py) ---

export type DealerPlateSource = "upload" | "generated"

export interface DealerPlate {
  id: number
  dealer_group_id: number
  name: string
  source: DealerPlateSource
  is_default: boolean
  thumbnail_url: string
  rooftops_using_this: number
  created_at: string
  updated_at: string
}

export interface DealerPlatesList {
  plates: DealerPlate[]
}

export interface DealerLogo {
  dealer_group_id: number
  logo_url: string
}

export interface RooftopPlateAssignment {
  rooftop_id: number
  rooftop_name: string
  rooftop_slug: string
  default_plate_id: number | null
  default_plate_name: string | null
}

export interface RooftopPlateAssignmentsList {
  assignments: RooftopPlateAssignment[]
}

// --- Fetchers ---

export async function getDealerLogo(dealerGroupId: number): Promise<DealerLogo | null> {
  // 404 from the backend means "no logo uploaded yet" — that's a
  // normal state for a brand-new dealer, not a failure mode. Surface
  // it as `null` so the view can render the empty state.
  const url = `${API.baseUrl}${API.endpoints.dealerPlates.logo(dealerGroupId)}`
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${getToken()}` },
  })
  if (res.status === 404) return null
  if (!res.ok) {
    const body = await res.text()
    throw new Error(body || `HTTP ${res.status}`)
  }
  return res.json()
}

export async function uploadDealerLogo(
  dealerGroupId: number, file: File,
): Promise<DealerLogo> {
  const formData = new FormData()
  formData.append("file", file)
  const res = await authFetchMultipart(
    `${API.baseUrl}${API.endpoints.dealerPlates.logo(dealerGroupId)}`,
    formData,
    "PUT",
  )
  return res.json()
}

export async function listDealerPlates(dealerGroupId: number): Promise<DealerPlatesList> {
  const res = await authFetch(
    `${API.baseUrl}${API.endpoints.dealerPlates.plates(dealerGroupId)}`,
  )
  return res.json()
}

export async function uploadDealerPlate(
  dealerGroupId: number, name: string, file: File,
): Promise<DealerPlate> {
  const formData = new FormData()
  formData.append("name", name)
  formData.append("file", file)
  const res = await authFetchMultipart(
    `${API.baseUrl}${API.endpoints.dealerPlates.upload(dealerGroupId)}`,
    formData,
    "POST",
  )
  return res.json()
}

export async function generateDealerPlate(
  dealerGroupId: number, name: string,
): Promise<DealerPlate> {
  const res = await authFetch(
    `${API.baseUrl}${API.endpoints.dealerPlates.generate(dealerGroupId)}`,
    { method: "POST", body: JSON.stringify({ name }) },
  )
  return res.json()
}

export async function updateDealerPlate(
  dealerGroupId: number, plateId: number,
  body: { name?: string; is_default?: boolean },
): Promise<DealerPlate> {
  const res = await authFetch(
    `${API.baseUrl}${API.endpoints.dealerPlates.plate(dealerGroupId, plateId)}`,
    { method: "PATCH", body: JSON.stringify(body) },
  )
  return res.json()
}

export async function deleteDealerPlate(
  dealerGroupId: number, plateId: number,
): Promise<void> {
  await authFetch(
    `${API.baseUrl}${API.endpoints.dealerPlates.plate(dealerGroupId, plateId)}`,
    { method: "DELETE" },
  )
}

export async function listRooftopPlateAssignments(
  dealerGroupId: number,
): Promise<RooftopPlateAssignmentsList> {
  const res = await authFetch(
    `${API.baseUrl}${API.endpoints.dealerPlates.rooftopAssignments(dealerGroupId)}`,
  )
  return res.json()
}

export async function setRooftopPlate(
  rooftopId: number, plateId: number | null,
): Promise<RooftopPlateAssignment> {
  const res = await authFetch(
    `${API.baseUrl}${API.endpoints.dealerPlates.rooftopPlate(rooftopId)}`,
    { method: "PATCH", body: JSON.stringify({ plate_id: plateId }) },
  )
  return res.json()
}
